import * as React from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Container,
  Stack,
  Typography,
} from "@mui/material";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import PhotoCameraOutlinedIcon from "@mui/icons-material/PhotoCameraOutlined";
import AccessTimeOutlinedIcon from "@mui/icons-material/AccessTimeOutlined";
import AssignmentTurnedInOutlinedIcon from "@mui/icons-material/AssignmentTurnedInOutlined";
import SmartphoneOutlinedIcon from "@mui/icons-material/SmartphoneOutlined";
import DesktopWindowsOutlinedIcon from "@mui/icons-material/DesktopWindowsOutlined";
import LocationOnOutlinedIcon from "@mui/icons-material/LocationOnOutlined";
import StorageOutlinedIcon from "@mui/icons-material/StorageOutlined";
import PersonOutlineOutlinedIcon from "@mui/icons-material/PersonOutlineOutlined";
import Lightbox from "yet-another-react-lightbox";
import "yet-another-react-lightbox/styles.css";
import { AppPublicLayout } from "../layout";

const layout = {
  page: {
    maxWidth: { content: 1200, wide: 1440 },
    paddingX: { xs: 2.5, md: 3.5 },
  },
  card: { radius: 2, padding: 3 },
} as const;

type SectionProps = {
  eyebrow?: string;
  title: React.ReactNode;
  description?: string;
  children?: React.ReactNode;
};

function Section({ eyebrow, title, description, children }: SectionProps) {
  return (
    <Box component="section" sx={{ py: { xs: 7, md: 10 } }}>
      <Container
        maxWidth={false}
        sx={{
          maxWidth: layout.page.maxWidth.content,
          px: layout.page.paddingX,
        }}
      >
        <Stack spacing={4}>
          <Stack spacing={1.5} sx={{ maxWidth: 780 }}>
            {eyebrow ? (
              <Typography
                variant="overline"
                color="primary"
                sx={{ fontWeight: 700, letterSpacing: 1 }}
              >
                {eyebrow}
              </Typography>
            ) : null}
            <Typography
              variant="h3"
              component="h2"
              sx={{ fontWeight: 750, letterSpacing: -0.8 }}
            >
              {title}
            </Typography>
            {description ? (
              <Typography
                variant="h6"
                color="text.secondary"
                sx={{ lineHeight: 1.65, fontWeight: 400 }}
              >
                {description}
              </Typography>
            ) : null}
          </Stack>
          {children}
        </Stack>
      </Container>
    </Box>
  );
}

function SoftCard({ children }: { children: React.ReactNode }) {
  return (
    <Card
      variant="outlined"
      elevation={0}
      sx={{
        height: "100%",
        borderRadius: layout.card.radius,
        backgroundImage: "none",
        borderColor: "divider",
      }}
    >
      <CardContent
        sx={{
          p: layout.card.padding,
          "&:last-child": { pb: layout.card.padding },
        }}
      >
        {children}
      </CardContent>
    </Card>
  );
}

function ScreenshotImage({
  src,
  alt,
  title,
  note,
  fit = "contain",
}: {
  src: string;
  alt: string;
  title: string;
  note: string;
  fit?: "contain" | "cover";
}) {
  const [open, setOpen] = React.useState(false);
  return (
    <Box>
      <Box
        component="button"
        type="button"
        onClick={() => setOpen(true)}
        aria-label={`${alt} vergrößert anzeigen`}
        sx={{
          width: "100%",
          p: 0,
          m: 0,
          textAlign: "left",
          appearance: "none",
          cursor: "zoom-in",
          aspectRatio: "16 / 10",
          borderRadius: 2.5,
          border: "1px solid",
          borderColor: "divider",
          overflow: "hidden",
          bgcolor: "background.paper",
          "&:hover": { borderColor: "primary.main" },
          "&:focus-visible": {
            outline: "3px solid",
            outlineColor: "primary.main",
            outlineOffset: 3,
          },
        }}
      >
        <Box
          component="img"
          src={src}
          alt={alt}
          loading="lazy"
          sx={{
            width: "100%",
            height: "100%",
            display: "block",
            objectFit: fit,
            objectPosition: "top center",
          }}
        />
      </Box>
      <Stack spacing={0.35} sx={{ mt: 1 }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
          {title}
        </Typography>
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ lineHeight: 1.5 }}
        >
          {note}
        </Typography>
      </Stack>
      <Lightbox
        open={open}
        close={() => setOpen(false)}
        slides={[{ src, alt }]}
        carousel={{ finite: true }}
        render={{ buttonPrev: () => null, buttonNext: () => null }}
      />
    </Box>
  );
}

function HeroVisual() {
  return (
    <Box
      sx={{
        width: "100%",
        maxWidth: { xs: "100%", md: 760, lg: 860 },
        ml: { md: "auto" },
      }}
    >
      <Box
        sx={{
          aspectRatio: "4 / 3",
          borderRadius: 4,
          border: "1px solid",
          borderColor: "divider",
          overflow: "hidden",
          bgcolor: "background.paper",
          boxShadow: { xs: 3, md: "0 28px 80px rgba(15, 23, 42, 0.14)" },
        }}
      >
        <Box
          component="img"
          src="/hero.png"
          alt="Logami Web-Oberfläche und Android-App für den SHK-Kundendienst"
          loading="eager"
          sx={{
            width: "100%",
            height: "100%",
            display: "block",
            objectFit: "cover",
          }}
        />
      </Box>
    </Box>
  );
}

function JobDataItem({ label }: { label: string }) {
  return (
    <Stack direction="row" spacing={1.25} alignItems="center">
      <CheckCircleOutlineIcon
        color="primary"
        sx={{ fontSize: 22, flexShrink: 0 }}
      />

      <Typography sx={{ fontWeight: 600 }}>{label}</Typography>
    </Stack>
  );
}

export function Home() {
  return (
    <AppPublicLayout cta={{ label: "Kostenlos testen", href: "/signup" }}>
      <Box component="main">
        <Box
          component="section"
          sx={{ py: { xs: 7, md: 11 }, overflow: "hidden" }}
        >
          <Container
            maxWidth={false}
            sx={{
              maxWidth: layout.page.maxWidth.wide,
              px: layout.page.paddingX,
            }}
          >
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: {
                  xs: "1fr",
                  md: "minmax(0, 0.92fr) minmax(560px, 1.08fr)",
                },
                gap: { xs: 5, md: 6 },
                alignItems: "center",
              }}
            >
              <Stack spacing={3.25} sx={{ maxWidth: 650 }}>
                <Stack spacing={1.75}>
                  <Chip
                    label="Für SHK-Betriebe · Kundendienst & Baustelle"
                    variant="outlined"
                    sx={{ alignSelf: "flex-start", borderRadius: 999 }}
                  />
                  <Typography
                    variant="h1"
                    sx={{
                      fontSize: { xs: 42, sm: 52, md: 60 },
                      lineHeight: 1.02,
                      fontWeight: 850,
                      letterSpacing: { xs: -1.2, md: -2.4 },
                    }}
                  >
                    Was draußen erledigt wird, kommt vollständig ins Büro.
                  </Typography>
                  <Typography
                    variant="h5"
                    color="text.secondary"
                    sx={{ lineHeight: 1.55, fontWeight: 400 }}
                  >
                    Zeiten, Fotos, Notizen und Unterschriften werden direkt vor
                    Ort erfasst und stehen im Büro sofort bereit. Beim
                    Kundendienst genauso wie auf mehrtägigen Baustellen.
                  </Typography>
                </Stack>
                <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5}>
                  <Button
                    size="large"
                    variant="contained"
                    href="/signup"
                    endIcon={<ArrowForwardIcon />}
                    sx={{
                      textTransform: "none",
                      borderRadius: 1.5,
                      px: 2.75,
                      py: 1.25,
                    }}
                  >
                    Kostenlos testen
                  </Button>
                  <Button
                    size="large"
                    variant="outlined"
                    href="mailto:kontakt@logami.de?subject=Demo%20vereinbaren"
                    sx={{
                      textTransform: "none",
                      borderRadius: 1.5,
                      px: 2.75,
                      py: 1.25,
                    }}
                  >
                    Demo vereinbaren
                  </Button>
                </Stack>
                <Typography variant="body2" color="text.secondary">
                  Kostenlos starten · Im Raum Köln gerne bei Ihnen vor Ort.
                </Typography>
              </Stack>
              <HeroVisual />
            </Box>
          </Container>
        </Box>

        <Box sx={{ bgcolor: "action.hover" }}>
          <Section
            eyebrow="Vom Einsatz ins Büro"
            title="Weniger Nachfragen. Weniger Nacharbeit. Schneller abrechnen."
            description="Wenn Zeiten, Fotos und Notizen an verschiedenen Stellen landen, fehlen im Büro schnell die Informationen zum Auftrag."
          >
            <Stack spacing={3} sx={{ maxWidth: 860 }}>
              <Stack spacing={0.5}>
                <Typography variant="h6" sx={{ fontWeight: 700 }}>
                  Mit Logami ist alles direkt am richtigen Auftrag.
                </Typography>

                <Typography color="text.secondary" sx={{ lineHeight: 1.5 }}>
                  Noch bevor der Monteur weiterfährt.
                </Typography>
              </Stack>
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: {
                    xs: "1fr",
                    sm: "repeat(2, 1fr)",
                    md: "repeat(3, 1fr)",
                  },
                  gap: 2,
                }}
              >
                <JobDataItem label="Arbeitszeiten" />
                <JobDataItem label="Fotos" />
                <JobDataItem label="Notizen" />
                <JobDataItem label="Mitarbeiter" />
                <JobDataItem label="Unterschriften" />
              </Box>
            </Stack>
          </Section>
        </Box>

        <Section
          eyebrow="So funktioniert Logami"
          title="Vom Auftrag bis zur fertigen Dokumentation."
          description="Für Kundendienst und mehrtägige Baustellen.
          Vom Anlegen des Auftrags bis zum Abschluss vor Ort."
        >
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", md: "repeat(3, 1fr)" },
              gap: 2,
            }}
          >
            <SoftCard>
              <Stack spacing={2}>
                <DesktopWindowsOutlinedIcon color="primary" />
                <Typography variant="h6" sx={{ fontWeight: 750 }}>
                  Auftrag vorbereiten
                </Typography>
                <Typography color="text.secondary">
                  Kunde, Einsatzort und Mitarbeiter festlegen.
                </Typography>
              </Stack>
            </SoftCard>
            <SoftCard>
              <Stack spacing={2}>
                <SmartphoneOutlinedIcon color="primary" />
                <Typography variant="h6" sx={{ fontWeight: 750 }}>
                  Vor Ort dokumentieren
                </Typography>
                <Typography color="text.secondary">
                  Zeiten, Fotos, Notizen und Unterschriften direkt beim Einsatz
                  erfassen.
                </Typography>
              </Stack>
            </SoftCard>
            <SoftCard>
              <Stack spacing={2}>
                <DescriptionOutlinedIcon color="primary" />
                <Typography variant="h6" sx={{ fontWeight: 750 }}>
                  Fertig fürs Büro
                </Typography>
                <Typography color="text.secondary">
                  Alles zum Auftrag steht direkt für die weitere Bearbeitung
                  bereit.
                </Typography>
              </Stack>
            </SoftCard>
          </Box>
          <Stack spacing={8}>
            <ScreenshotImage
              src="/auftrag_erstellen_web.png"
              alt="Web-Oberfläche zur Auftragserfassung in Logami"
              title="Auftrag vorbereiten"
              note="Im Büro stehen Kunde, Einsatzort und Mitarbeiter für den Einsatz bereit."
              fit="cover"
            />
            <ScreenshotImage
              src="/android_demo.png"
              alt="Logami Android-App für SHK-Monteure"
              title="Vor Ort dokumentieren"
              note="Alles Wichtige wird direkt beim Einsatz festgehalten."
            />
            <ScreenshotImage
              src="/auftrag_bericht_web.png"
              alt="Web-Bericht eines abgeschlossenen Einsatzes in Logami"
              title="Fertig fürs Büro"
              note="Zeiten, Fotos, Notizen und Unterschriften liegen gesammelt am Auftrag vor."
              fit="cover"
            />
          </Stack>
        </Section>

        <Box sx={{ bgcolor: "action.hover" }}>
          <Section
            eyebrow="Was Logami einfacher macht"
            title="Einfach draußen. Vollständig drinnen."
          >
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "1fr", sm: "repeat(2, 1fr)" },
                gap: 2,
              }}
            >
              <SoftCard>
                <Stack spacing={2}>
                  <AccessTimeOutlinedIcon color="primary" />
                  <Typography variant="h6" sx={{ fontWeight: 750 }}>
                    Schnell vor Ort erledigt
                  </Typography>
                  <Typography color="text.secondary">
                    Zeiten, Fotos, Notizen und Unterschriften mit wenigen
                    Schritten erfassen.
                  </Typography>
                </Stack>
              </SoftCard>
              <SoftCard>
                <Stack spacing={2}>
                  <AssignmentTurnedInOutlinedIcon color="primary" />
                  <Typography variant="h6" sx={{ fontWeight: 750 }}>
                    Alles direkt am Auftrag
                  </Typography>
                  <Typography color="text.secondary">
                    Im Büro sind die wichtigen Informationen vollständig und an
                    einer Stelle.
                  </Typography>
                </Stack>
              </SoftCard>
              <SoftCard>
                <Stack spacing={2}>
                  <PhotoCameraOutlinedIcon color="primary" />
                  <Typography variant="h6" sx={{ fontWeight: 750 }}>
                    Für Kundendienst und Baustelle
                  </Typography>
                  <Typography color="text.secondary">
                    Vom kurzen Reparatureinsatz bis zur mehrtägigen Baustelle in
                    einem System.
                  </Typography>
                </Stack>
              </SoftCard>
              <SoftCard>
                <Stack spacing={2}>
                  <SmartphoneOutlinedIcon color="primary" />
                  <Typography variant="h6" sx={{ fontWeight: 750 }}>
                    Auch ohne Empfang nutzbar
                  </Typography>
                  <Typography color="text.secondary">
                    Die Android-App funktioniert offline und synchronisiert
                    automatisch, sobald wieder eine Verbindung besteht.
                  </Typography>
                </Stack>
              </SoftCard>
            </Box>
          </Section>
        </Box>

        <Section
          eyebrow="Passt zu Ihrer Bürosoftware"
          title="Logami ergänzt, was Sie bereits nutzen."
          description="Ihre Bürosoftware bleibt, wie sie ist.
          Logami übernimmt die Dokumentation vor Ort und bringt
          alle Informationen gesammelt zurück ins Büro."
        >
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", md: "repeat(3, 1fr)" },
              gap: 2,
            }}
          >
            <SoftCard>
              <Stack spacing={2}>
                <DesktopWindowsOutlinedIcon color="primary" />
                <Typography variant="h6" sx={{ fontWeight: 750 }}>
                  Bürosoftware behalten
                </Typography>
                <Typography color="text.secondary">
                  Kein Systemwechsel nur für die Dokumentation vor Ort.
                </Typography>
              </Stack>
            </SoftCard>
            <SoftCard>
              <Stack spacing={2}>
                <DescriptionOutlinedIcon color="primary" />
                <Typography variant="h6" sx={{ fontWeight: 750 }}>
                  Alles kommt zusammen
                </Typography>
                <Typography color="text.secondary">
                  Zeiten, Fotos, Notizen und Unterschriften stehen gesammelt am
                  Auftrag bereit.
                </Typography>
              </Stack>
            </SoftCard>
            <SoftCard>
              <Stack spacing={2}>
                <CheckCircleOutlineIcon color="primary" />
                <Typography variant="h6" sx={{ fontWeight: 750 }}>
                  Einfach weiterarbeiten
                </Typography>
                <Typography color="text.secondary">
                  Die Dokumentation steht im Büro für die weitere Bearbeitung
                  zur Verfügung.
                </Typography>
              </Stack>
            </SoftCard>
          </Box>
        </Section>

        <Box sx={{ bgcolor: "action.hover" }}>
          <Section
            eyebrow="Über Logami"
            title="Persönlich entwickelt und betreut in Köln."
          >
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: {
                  xs: "1fr",
                  md: "340px minmax(0, 1fr)",
                },
                gap: { xs: 3, md: 10 },
              }}
            >
              <Box
                aria-label="Platzhalter für Gründerfoto"
                sx={{
                  width: { xs: 180, md: 260 },
                  aspectRatio: "1",
                  borderRadius: 3,
                  display: "grid",
                  placeItems: "center",
                  bgcolor: "background.paper",
                  border: "1px solid",
                  borderColor: "divider",
                  color: "text.secondary",
                  fontSize: 13,
                  textAlign: "center",
                  px: 2,
                  justifySelf: { xs: "start", md: "center" },
                }}
              >
                Gründerfoto
              </Box>

              <Stack spacing={2.5}>
                <Stack spacing={0.25}>
                  <Typography variant="h5" sx={{ fontWeight: 750 }}>
                    Lukas Ottweiler
                  </Typography>
                  <Typography color="text.secondary">
                    Gründer & Entwickler
                  </Typography>
                </Stack>

                <Typography
                  color="text.secondary"
                  sx={{
                    lineHeight: 1.7,
                    maxWidth: 680,
                  }}
                >
                  Logami soll jedem Betrieb ein gutes Werkzeug für die
                  Dokumentation vor Ort an die Hand geben, damit mehr Zeit für
                  die Arbeit bleibt, auf die es ankommt.
                </Typography>

                <Typography
                  color="text.secondary"
                  sx={{
                    lineHeight: 1.7,
                    maxWidth: 680,
                  }}
                >
                  Im Raum Köln komme ich gerne persönlich vorbei, zeige Ihnen
                  Logami im Einsatz und richte es gemeinsam mit Ihnen ein.
                </Typography>

                <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                  <Chip
                    icon={<LocationOnOutlinedIcon />}
                    label="Entwickelt in Köln"
                    size="small"
                  />
                  <Chip
                    icon={<StorageOutlinedIcon />}
                    label="Daten in Deutschland"
                    size="small"
                  />
                  <Chip
                    icon={<PersonOutlineOutlinedIcon />}
                    label="Direkter Ansprechpartner"
                    size="small"
                  />
                </Stack>

                <Button
                  size="large"
                  variant="outlined"
                  href="mailto:kontakt@logami.de?subject=Demo%20vereinbaren"
                  sx={{
                    alignSelf: "flex-start",
                    textTransform: "none",
                    borderRadius: 1.5,
                  }}
                >
                  Demo vereinbaren
                </Button>
              </Stack>
            </Box>
          </Section>
        </Box>

        <Section
          eyebrow="Preis"
          title={
              <>
                12 € pro Nutzer / Monat
                <Box
                  component="span"
                  sx={{
                    fontSize: "0.55em",
                    color: "text.secondary",
                    verticalAlign: "super",
                    ml: 0.45,
                  }}
                >
                  *
                </Box>
              </>
            }
          description="Ein Preis pro Nutzer im Büro und unterwegs."
        >
          <Stack spacing={4}>
            <Stack spacing={2.5}  >
              <Typography variant="h6" sx={{ fontWeight: 750 }}>
                Alles inklusive
              </Typography>

              <Box
                sx={{
                  display: "grid",
                  width: "100%",
                  gridTemplateColumns: {
                    xs: "1fr",
                    sm: "repeat(2, minmax(0, 1fr))",
                    md: "repeat(3, minmax(0, 1fr))",
                  },
                  columnGap: { sm: 4, md: 15 },
                  rowGap: 1.75,
                }}
              >
                {[
                  "Web-Oberfläche fürs Büro",
                  "Android-App für Monteure",
                  "Kundendienst",
                  "Mehrtägige Baustellen",
                  "Arbeits- und Pausenzeiten",
                  "Fotos und Notizen",
                  "Mitarbeiter am Einsatz",
                  "Digitale Unterschriften",
                  "Einsatzberichte",
                  "Offline-Nutzung",
                  "Automatische Synchronisation",
                  "Tägliche Backups",
                  "Persönlicher Support",
                ].map((item) => (
                  <Stack
                    key={item}
                    direction="row"
                    spacing={1}
                    alignItems="center"
                  >
                    <CheckCircleOutlineIcon
                      color="primary"
                      sx={{
                        fontSize: 20,
                        flexShrink: 0,
                      }}
                    />

                    <Typography color="text.secondary">
                      {item}
                    </Typography>
                  </Stack>
                ))}
              </Box>
            </Stack>

            <Box
              sx={{
                pt: 3.5,
                borderTop: "1px solid",
                borderColor: "divider",
              }}
            >
              <Stack
                direction={{ xs: "column", md: "row" }}
                spacing={{ xs: 3, md: 4 }}
                alignItems={{ xs: "flex-start", md: "center" }}
                justifyContent="space-between"
              >
                <Stack spacing={0.5}>
                  <Typography sx={{ fontWeight: 750 }}>
                    Beispiel: 3 Monteure + 1 Büro-Nutzer = 4 Nutzer = 48 € / Monat*
                  </Typography>

                  <Typography variant="caption" color="text.secondary">
                    * zzgl. MwSt.
                  </Typography>
                </Stack>

                <Stack alignItems="flex-start" spacing={0.5}>
                  <Button
                    size="large"
                    variant="contained"
                    href="/signup"
                    endIcon={<ArrowForwardIcon />}
                    sx={{
                      textTransform: "none",
                      borderRadius: 1.5,
                    }}
                  >
                    Kostenlos testen
                  </Button>

                  <Typography variant="body2" color="text.secondary">
                    14 Tage unverbindlich
                  </Typography>
                </Stack>
              </Stack>
            </Box>
          </Stack>
        </Section>

        <Box sx={{ bgcolor: "action.hover" }}>
          <Section
            eyebrow="Jetzt starten"
            title="Mehr Zeit für die Arbeit, auf die es ankommt."
            description="Starten Sie direkt online oder lernen Sie Logami in einer persönlichen Demo kennen."
          >
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: {
                  xs: "1fr",
                  md: "repeat(2, minmax(0, 1fr))",
                },
                gap: { xs: 3, md: 5 },
                alignSelf: "center",
                width: "100%",
              }}
            >
              <Stack spacing={1}>
                <Button
                  size="large"
                  variant="contained"
                  href="/signup"
                  endIcon={<ArrowForwardIcon />}
                  sx={{
                    alignSelf: "flex-start",
                    textTransform: "none",
                    borderRadius: 1.5,
                  }}
                >
                  Kostenlos testen
                </Button>

                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ lineHeight: 1.55 }}
                >
                  14 Tage unverbindlich · Direkt online starten
                </Typography>
              </Stack>

              <Stack spacing={1}>
                <Button
                  size="large"
                  variant="outlined"
                  href="mailto:kontakt@logami.de?subject=Demo%20vereinbaren"
                  sx={{
                    alignSelf: "flex-start",
                    textTransform: "none",
                    borderRadius: 1.5,
                  }}
                >
                  Demo vereinbaren
                </Button>

                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{
                    lineHeight: 1.55,
                    maxWidth: 430,
                  }}
                >
                  Persönlich kennenlernen und Fragen klären. Im Raum Köln gerne bei
                  Ihnen vor Ort.
                </Typography>
              </Stack>
            </Box>
          </Section>
        </Box>
      </Box>
    </AppPublicLayout>
  );
}

export default Home;
